import { Autocomplete as MUIAutocomplete, FilterOptionsState, FormHelperText, MenuItem, TextField } from '@mui/material';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { GMSelectOption } from '../../Select';
import css from './Autocomplete.module.scss';
import classNames from 'classnames';
import ClearIcon from '@mui/icons-material/Clear';
import { useLocalization } from '../../../../Contexts/Localization';
import { GMTooltip } from '../../../Tooltip/Tooltip';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { GMOutlinedInputLabel } from '../../InputLabel';
import { ErrorLevel } from '../../../../API/Enums';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { GMAutocompleteProps } from '..';
import { NumericMask } from './NumericMask/NumericMask';
import { VirtualizedList } from './VirtualizedList/VirtualizedList';
import { GMInputWrapper } from '../../InputWrapper';

export const VIRTUALIZATION_THRESHOLD = 200;

const Autocomplete: GoMotoComponent<GMAutocompleteProps> = memo(({
    label,
    options,
    value,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    clearable = true,
    disableKeyboardInput,
    noOptionsText,
    numericOnly,
    helperText,
    autoSelect,
    autoHighlight,
    ...wrapperProps
}) => {
    const Localization = useLocalization();
    const selected: GMSelectOption | null = options.find((o) => o.value === value) ?? null;
    const [inputValue, setInputValue] = useState<string>('');

    useEffect(() => {
        setInputValue(selected ? selected.label : '');
    }, [selected]);

    const onSelectChange = (_e: React.SyntheticEvent, option: GMSelectOption | null) => {
        setInputValue(option ? option.label : '');
        onChange(option ? option.value : '');
    };

    const errorState: boolean = wrapperProps.error !== undefined && wrapperProps.error !== ErrorLevel.None;

    return (
        <GMInputWrapper
            {...wrapperProps}
        >
            {wrapperProps.variant === 'outlined' ?
                <GMOutlinedInputLabel
                    label={label}
                    required={wrapperProps.required}
                    error={wrapperProps.error}
                    disabled={wrapperProps.disabled}
                    color={wrapperProps.color}
                /> : null
            }
            <MUIAutocomplete
                className={classNames(
                    css.root,
                    !clearable ? css.notClearable : undefined,
                    disableKeyboardInput ? css.noKeyboardInput : undefined,
                )}
                autoSelect={autoSelect}
                autoHighlight={autoHighlight}
                options={options}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        onChange={(e) => setInputValue(e.target.value)}
                        label={wrapperProps.variant === 'standard' ? label : ''}
                        variant={wrapperProps.variant}
                        error={errorState}
                        required={wrapperProps.required}
                        placeholder={placeholder}
                        inputProps={{
                            ...params.inputProps,
                            readOnly: disableKeyboardInput,
                            value: inputValue,
                        }}
                        InputProps={{
                            ...params.InputProps,
                            inputComponent: numericOnly ? NumericMask : undefined,
                        }}
                    />
                )}
                classes={{
                    option: css.option,
                }}
                noOptionsText={noOptionsText ?? Localization.NoOptions}
                disableClearable={!clearable}
                disabled={wrapperProps.disabled}
                onFocus={onFocus}
                onChange={onSelectChange}
                onBlur={onBlur}
                onOpen={() => setInputValue('')}
                onClose={(_, reason) => {
                    if (reason !== 'selectOption') {
                        setInputValue(selected ? selected.label : '');
                    }
                }}
                selectOnFocus={disableKeyboardInput ? false : undefined}
                value={selected}
                isOptionEqualToValue={(option: GMSelectOption, value: GMSelectOption) => {
                    return option.value === value.value;
                }}
                ListboxComponent={options.length > VIRTUALIZATION_THRESHOLD ? VirtualizedList : undefined}
                renderOption={options.length > VIRTUALIZATION_THRESHOLD ?
                    (optionProps, option) => [optionProps, option] as React.ReactNode :
                    (optionProps, option) => (
                        <MenuItem value={option.value} {...optionProps}>
                            {option.menuOptionLabel ? option.menuOptionLabel : option.label}
                        </MenuItem>
                    )
                }
                clearText={''}
                closeText={''}
                openText={''}
                clearIcon={
                    <GMTooltip
                        text={Localization.Clear}
                    >
                        <ClearIcon fontSize={'small'} />
                    </GMTooltip>
                }
                popupIcon={
                    <ArrowDropDownIcon />
                }
                filterOptions={useCallback((options: GMSelectOption[], state: FilterOptionsState<GMSelectOption>) => {
                    return options.filter((option) => (option.menuOptionLabel ?? option.label).toLowerCase()
                        .includes(state.inputValue.toLowerCase()));
                }, [])}
            />
            {helperText ?
                <FormHelperText
                    error={errorState}
                    disabled={wrapperProps.disabled}
                >
                    {helperText}
                </FormHelperText>
                : null
            }
        </GMInputWrapper>
    );
});

export default Autocomplete;